import { Component, computed, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmDeleteProjectModalComponent } from "@components/confirm-delete-project-modal/confirm-delete-project-modal.component";
import { InvitePersonToProjectModalComponent } from "@components/invite-person-to-project-modal/invite-person-to-project-modal.component";
import { ProjectVersionConflictModalComponent } from "@components/project-version-conflict-modal/project-version-conflict-modal.component";
import { ProjectVersionModalComponent } from "@components/project-version-modal/project-version-modal.component";
import { ProductService } from "@domain/product/product.service";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { ProjectResponseDto } from "@domain/project/api/project-response.dto";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageService } from "@domain/project/project-image.service";
import { ProjectStatusIconMapperService } from "@domain/project/project-status-icon-mapper.service";
import { ProjectStatusService } from "@domain/project/project-status.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ChipComponent } from "@odx/angular/components/chip";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import * as Sentry from "@sentry/angular";
import { ReadAccessModalService } from "@utils/modals/read-access-modal.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { filter, forkJoin, map, Observable, of, switchMap, tap, throwError } from "rxjs";
import { take } from "rxjs/operators";
import { LocalizeDatePipe } from "src/locale/localize-date";
import { LocalizeTimePipe } from "src/locale/localize-time";

@Component({
  selector: "app-project-card",
  templateUrl: "./project-card.component.html",
  styleUrls: ["./project-card.component.scss"],
  imports: [UiKitModule, LocalizeDatePipe, LocalizeTimePipe, ChipComponent],
  standalone: true,
})
export class ProjectCardComponent implements OnInit {
  private projectService = inject(ProjectService);
  private toastHelper = inject(ToastHelper);
  private router = inject(Router);
  private projectApiService = inject(ProjectApiService);
  private readAccessModalService = inject(ReadAccessModalService);
  private productService = inject(ProductService);
  private userService = inject(UserService);
  private projectStatusService: ProjectStatusService = inject(ProjectStatusService);
  private projectStatusIconMapperService: ProjectStatusIconMapperService = inject(ProjectStatusIconMapperService);
  private projectImageService = inject(ProjectImageService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  @Input() project!: ScopedProject;

  @Input() showMenu: boolean = true;

  @Input() linkToProject: boolean = true;

  @Output() deleteEmitter$ = new EventEmitter();

  protected isCollaborationProject = false;
  protected readonly navigator = navigator;
  protected hasWritePermission$!: Observable<boolean>;
  protected statusIcon$: Observable<string> = this.getStatusIcon();

  private readonly modalService = inject(ModalService);
  readonly user = toSignal(this.userService.user$);
  readonly userScope = computed(() => this.user()?.scope);
  readonly isMonitoringScope = computed(() => this.userScope() === BusinessScope.MONITORING);
  readonly draegerContactPerson = computed(() => {
    if (this.project instanceof MonitoringProject) {
      return this.project.draegerContactPerson;
    }
    return undefined;
  });

  ngOnInit(): void {
    this.hasWritePermission$ = this.projectService.checkUserWritePermission(this.project);

    if (this.project.cloudId) {
      this.projectService
        .getLocalMetadataById(this.project.cloudId)
        .pipe(
          take(1),
          map((metadata) => (this.isCollaborationProject = metadata ? metadata.users.length > 1 : false)),
        )
        .subscribe();
    }
  }

  protected exportProjectToPdf(project: ScopedProject) {
    this.productService.updateFilterCountry(project.country);
    this.modalService.open(ProjectVersionModalComponent, { data: { project: project } }).onClose$.subscribe();
  }

  protected onClick() {
    if (!this.project.cloudId || !navigator.onLine) {
      this.projectService.updateUserWritePermission(this.project).subscribe();
      this.navigateToProject();
      return;
    }
    if (navigator.onLine) {
      this.handleMetadataSync()
        .pipe(take(1))
        .subscribe({
          next: () => this.navigateToProject(),
          error: (error) => {
            // Navigating here would open the stale local project the user just chose to discard.
            console.error("Failed to resolve project version conflict:", error);
            this.toastHelper.error(
              $localize`:@@toast.project.versionConflict.error.header:Projektversion konnte nicht geladen werden`,
              $localize`:@@toast.project.versionConflict.error.description:Die Cloud-Version konnte nicht übernommen werden. Bitte versuche es erneut.`,
            );
          },
        });
    }
  }

  protected onDelete() {
    this.modalService
      .open(ConfirmDeleteProjectModalComponent, { data: { project: this.project } })
      .onClose$.pipe(
        switchMap((shouldDeleteFromCloud) => {
          if (shouldDeleteFromCloud && this.project.cloudId) {
            this.projectApiService.deleteProjectFromCloud(this.project.cloudId).subscribe();
          }
          return forkJoin([
            this.projectService.deleteLocalMetadata(this.project.cloudId),
            this.projectService.deleteProject(this.project),
            this.projectImageService.deleteProjectImagesFromIndexedDB(this.project.id),
            this.projectFloorplanService.deleteProjectFloorplansFromIndexedDB(this.project.id),
          ]);
        }),
      )
      .subscribe(() => this.deleteEmitter$.emit());
  }

  protected openInvitePersonModal() {
    this.modalService
      .open(InvitePersonToProjectModalComponent, {
        size: ModalSize.MEDIUM,
      })
      .onClose$.pipe(
        filter((value) => !!value && !!this.project?.cloudId),
        switchMap((value) => this.projectApiService.inviteCollaborators(value, this.project.cloudId)),
      )
      .subscribe((response) => {
        const errors = response
          .filter((res: { success: boolean; message: string; to?: string }) => {
            return !res.success;
          })
          .map((res: { success: boolean; message: string; to?: string }) => res.to);

        if (errors.length >= 1) {
          this.toastHelper.error(
            $localize`:@@toast.invitation.error:Einladung konnte nicht verschickt werden an: ` + errors.join(", "),
          );
        } else {
          this.toastHelper.success($localize`:@@toast.invitation.successful:Einladung wurde erfolgreich verschickt`);
        }
      });
  }

  protected openVersionConflictModal(cloudMetadata: CloudProjectMetadata): Observable<ScopedProject | ProjectResponseDto> {
    return this.modalService
      .open(ProjectVersionConflictModalComponent, {
        data: {
          projectName: cloudMetadata.name,
          updatedBy: cloudMetadata.updatedBy,
          lastModifiedOn: cloudMetadata.updatedAt,
        },
      })
      .onClose$.pipe(
        tap((value: string) => {
          Sentry.captureMessage(`[version-conflict] USER-CHOICE projectId=${this.project.cloudId} choice=${value}`, "info");
        }),
        switchMap((value: string) => {
          switch (value) {
            case "saveCopy": {
              const projectCopy = requireScopedProject(this.project.copy());

              // Images and floorplans are copied first (getting new ids in the process), so that the group /
              // product item references on the project copy can be remapped to the new floorplan ids before the
              // copy is persisted. This ensures groups are correctly carried over to the copied project.
              return this.projectImageService.copyImages(this.project.id, projectCopy, this.project.cloudId).pipe(
                switchMap((imageIdMap) => {
                  return this.projectFloorplanService.copyFloorplans(this.project.id, projectCopy, imageIdMap);
                }),
                tap((floorplanIdMap) => {
                  projectCopy.remapFloorplanReferences(floorplanIdMap);
                }),
                switchMap(() => {
                  return this.projectService.createProject(projectCopy);
                }),
                switchMap(() => {
                  return this.projectService.updateLocalVersion(cloudMetadata);
                }),
                map((updatedProject) => {
                  return (this.project = updatedProject);
                }),
              );
            }
            case "openNewVersion": {
              return this.projectService.updateLocalVersion(cloudMetadata).pipe(
                map((updatedProject) => {
                  return (this.project = updatedProject);
                }),
              );
            }
            case "overwriteNewVersion": {
              // overwriteCloudVersion() also pushes the local floorplans; exporting the project alone would
              // leave the cloud floorplans in place and they would be pulled over the local ones on open.
              return this.projectService.overwriteCloudVersion(this.project);
            }
            default:
              throw new Error("Modal was closed or cancelled without a valid action");
          }
        }),
      );
  }

  private getStatusIcon(): Observable<string> {
    return this.userService.businessScope$.pipe(
      switchMap((scope) => this.projectStatusService.getProjectStatuses(scope)),
      map((statuses) => this.projectStatusIconMapperService.mapIcon(this.project.status, statuses)),
    );
  }

  private navigateToProject(): void {
    if (this.linkToProject) {
      const navigationPath = `/projects/${this.project.id}`;
      this.router.navigate([navigationPath]);
    }
  }

  private handleMetadataSync(): Observable<ScopedProject | ProjectResponseDto | undefined> {
    return forkJoin([
      this.projectService.getLocalMetadataById(this.project.cloudId!),
      this.projectApiService.getMetadata(this.project.cloudId!),
    ]).pipe(
      switchMap(([localMetadata, cloudMetadata]) => {
        // getLocalMetadataById() resolves to undefined when no metadata record exists,
        // so both guards have to run before any property of localMetadata is read.
        if (!localMetadata && !cloudMetadata) {
          return this.projectService.exportProjectToCloud(this.project);
        }
        if (localMetadata && localMetadata.businessScope !== this.userScope()) {
          console.error("ScopedProject scope does not fit user scope");
          return throwError(() => new Error("ScopedProject scope doesn't match user scope"));
        }
        if (localMetadata === cloudMetadata) {
          return this.projectService
            .findLocalProjectByCloudId(localMetadata.id)
            .pipe(filter((project): project is ScopedProject => project !== undefined));
        }
        if (!localMetadata && cloudMetadata) {
          localMetadata = cloudMetadata;
        }
        if (!cloudMetadata) {
          console.error("No cloud metadata found for project that has local metadata set");
          return of(undefined);
        }
        return this.projectService.updateUserWritePermission(this.project).pipe(
          take(1),
          switchMap((hasWritePermission) => {
            if (hasWritePermission && !this.isProjectHashEqual(localMetadata, cloudMetadata!)) {
              return this.openVersionConflictModal(cloudMetadata!);
            }
            if (hasWritePermission && this.isProjectHashEqual(localMetadata, cloudMetadata)) {
              return this.projectService.updateLocalMetadata(cloudMetadata).pipe(
                map(() => {
                  return this.project;
                }),
              );
            }
            if (!hasWritePermission && !this.isProjectHashEqual(localMetadata, cloudMetadata!)) {
              return this.projectService.updateLocalVersion(cloudMetadata!).pipe(
                map((updatedProject) => {
                  this.project = updatedProject;
                  return updatedProject;
                }),
              );
            }
            return of(undefined);
          }),
        );
      }),
    );
  }

  private isProjectHashEqual(localMetadata: CloudProjectMetadata, cloudMetadata: CloudProjectMetadata): boolean {
    if (!localMetadata || !cloudMetadata) {
      return false;
    }
    const equal = localMetadata.projectHash === cloudMetadata.projectHash;
    if (!equal) {
      Sentry.captureMessage(
        `[version-conflict] HASH-MISMATCH projectId=${this.project.cloudId} localHash=${localMetadata.projectHash} cloudHash=${cloudMetadata.projectHash} localUpdatedAt=${localMetadata.updatedAt} cloudUpdatedAt=${cloudMetadata.updatedAt}`,
        "warning",
      );
    }
    return equal;
  }

  get offlineToolTipMessage() {
    return !navigator.onLine
      ? $localize`:@@project.online.requiredOnline:Einige Funktionen sind nur im Online-Modus verfügbar.`
      : "";
  }

  openShareLinkModal() {
    this.readAccessModalService.openModal(this.project);
  }
}
